/**
 * Hook for managing chat sessions with REST + observer WebSocket.
 *
 * The server owns turn execution (shared/chat/turn_runner.py). This hook:
 *   - drives turns via POST /sessions/{id}/messages and /interrupt
 *   - opens a read-only WebSocket that replays history on connect and
 *     forwards runner events (turn_started, text_delta, tool_start,
 *     tool_end, done, interrupted, approval_required, error, error_notice)
 *   - reconnects with ?since=<last_message_id> so nothing is lost
 *
 * The client never sends driving messages over WS.
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { useQueryClient, onlineManager } from '@tanstack/react-query';
import axiosInstance, { getValidAccessToken } from '../api/axiosInstance';
import { getWebSocketBaseUrl } from '../api/config';
import { WebSocketCloseCode } from '../notifications';
import type { ChatMessage, StreamingState, ToolEvent, ToolCallData } from '../components/chat/types';
import { mergeMessagesBySeq } from '../components/chat/chatMessageStore';
import { translateErrorCode } from '../utils/errors';

/**
 * Monotonic suffix for client-generated system-message ids.
 *
 * `Date.now()` is not enough: a batch of tool calls that fail together emits
 * one `error_notice` per failure in the same synchronous loop, so several land
 * inside one millisecond. `ChatTranscript` keys the message list by `id`, and
 * duplicate React keys break reconciliation (a bubble silently fails to render).
 */
let noticeIdCounter = 0;
const nextNoticeId = (): string => `${Date.now()}-${++noticeIdCounter}`;

/** Chat session state */
interface ChatSessionState {
  sessionId: string | null;
  chatType: 'agent' | 'assistant' | 'avatar';
  status: 'idle' | 'connecting' | 'active' | 'error';
  messages: ChatMessage[];
  error: string | null;
  enabledModules: string[];
  acceptedMimeTypes: string[];
}

interface ChatSessionOptions {
  chatType?: 'agent' | 'assistant' | 'avatar';
  context?: Record<string, unknown>;
  aboutEntities?: string[];
  title?: string;
  sessionId?: string;
  /** @deprecated — streaming is always on via observer WS. Kept for caller compatibility. */
  stream?: boolean;
  modelCategory?: string | null;
}

interface UseChatSessionReturn {
  session: ChatSessionState;
  streaming: StreamingState;
  createSession: (options?: ChatSessionOptions) => Promise<string | null>;
  loadSession: (sessionId: string) => Promise<void>;
  sendMessage: (message: string, messageContext?: Record<string, unknown>, fileIds?: string[], overrideSessionId?: string) => Promise<void>;
  acceptedMimeTypes: string[];
  stopGeneration: () => Promise<void>;
  endSession: () => Promise<void>;
  reset: () => void;
  sessionContextRef: React.MutableRefObject<Record<string, unknown> | null>;
  pendingModelCategoryRef: React.MutableRefObject<string | null>;
  wsRef: React.RefObject<WebSocket | null>;
  injectEvent: (event: Record<string, unknown>) => void;
  updateToolCallStatus: (toolCallId: string, status: ToolCallData['status']) => void;
}

function buildWsUrl(sessionId: string, token: string, since: string | null): string {
  const sinceParam = since ? `&since=${encodeURIComponent(since)}` : '';
  return `${getWebSocketBaseUrl()}/api/v1/ai-chat/ws/${sessionId}?token=${encodeURIComponent(token)}${sinceParam}`;
}

const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_BASE_DELAY_MS = 1000;
// A stale access token on a long-lived socket is not a network failure, so it
// shouldn't eat into MAX_RECONNECT_ATTEMPTS. We refresh + reconnect immediately
// instead, bounded so a genuinely revoked session can't spin forever before
// falling back to the normal (eventually-exhausting) reconnect path.
const MAX_AUTH_REFRESH_ATTEMPTS = 2;

export function useChatSession(defaultOptions?: ChatSessionOptions): UseChatSessionReturn {
  const queryClient = useQueryClient();

  const [session, setSession] = useState<ChatSessionState>({
    sessionId: defaultOptions?.sessionId || null,
    chatType: defaultOptions?.chatType || 'assistant',
    status: defaultOptions?.sessionId ? 'active' : 'idle',
    messages: [],
    error: null,
    enabledModules: [],
    acceptedMimeTypes: [],
  });

  const [streaming, setStreaming] = useState<StreamingState>({
    response: undefined,
    isGenerating: false,
  });

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptsRef = useRef(0);
  // Consecutive token-refresh reconnects since the last successful open. Reset
  // in ws.onopen; bounds the auth-expiry reconnect path (MAX_AUTH_REFRESH_ATTEMPTS).
  const authRefreshAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Abort the in-flight connect attempt when a new one starts or when we tear
  // down. Prevents the StrictMode double-mount race where two `connectWebSocket`
  // calls both resolve their token `await` and each create a WebSocket — both
  // bound to the same handlers, so every `text_delta` is applied twice.
  const connectAbortRef = useRef<AbortController | null>(null);

  // Streaming state refs (avoid stale closures inside WS handler)
  const fullResponseRef = useRef('');
  const reasoningRef = useRef('');
  const toolEventsRef = useRef<ToolEvent[]>([]);
  const isGeneratingRef = useRef(false);
  const currentTurnIdRef = useRef<string | null>(null);
  const sendResolveRef = useRef<(() => void) | null>(null);
  // Highest server seq already reflected in the rendered state for the current
  // turn — set when a mid-turn `turn_snapshot` is consumed (B1 resumable
  // streaming). Live text/reasoning deltas at or below this seq for the same
  // turn are dropped so the partial output rendered from the snapshot isn't
  // duplicated. -1 means "no snapshot for this turn" (apply every delta).
  const snapshotSeqRef = useRef<number>(-1);

  // Track last known message id so a reconnect can replay only new messages
  const lastMessageIdRef = useRef<string | null>(null);

  const sessionContextRef = useRef<Record<string, unknown> | null>(null);
  const pendingModelCategoryRef = useRef<string | null>(null);

  // `defaultOptions.sessionId` is the ONE external input that owns the active
  // session (supplied by the caller's single source of truth — see
  // useChatRouting in AIChatSidePanel). The WS lifecycle below is a
  // deterministic, idempotent function of it: (re)connect on mount/change,
  // close on null/unmount.
  //
  // Track the last sessionId PROP we observed so we can detect prop-driven
  // session switches (e.g., user clicks "New Chat" and the parent sets a new
  // id via URL). We don't compare against `session.sessionId` because that
  // field also updates when createSession() lazily creates a session for the
  // first user message — and wiping state in that case would drop the new
  // session ID before it can be used.
  const lastPropSessionIdRef = useRef<string | null>(defaultOptions?.sessionId || null);

  // Mirror of session.sessionId for synchronous reads inside the prop-change
  // effect below. Kept in sync on every render so it never lags the effect's
  // closure (the effect omits session.sessionId from its deps on purpose).
  const sessionIdRef = useRef(session.sessionId);
  sessionIdRef.current = session.sessionId;

  useEffect(() => {
    const newSessionId = defaultOptions?.sessionId || null;
    const changed = newSessionId !== lastPropSessionIdRef.current;
    lastPropSessionIdRef.current = newSessionId;
    // Reset on a genuine session switch, but NOT when our local state already
    // reflects the incoming id. The latter happens on the FIRST message of a
    // brand-new chat: sendMessage() (enhanced mode, overrideSessionId) sets
    // session.sessionId + the optimistic user message and opens the WS, then the
    // parent syncs activeSessionId → URL ?chat= → back as this prop. Resetting
    // there would wipe that optimistic message and tear down the live WS — the
    // user's first message would vanish until a page refresh. (Falls through to
    // the connect guard below, a no-op when the WS is already open.)
    if (changed && newSessionId !== sessionIdRef.current) {
      // Fully reset streaming state so nothing from the previous session
      // (thinking indicator, partial response, tool events, pending send
      // promises, stale WebSocket) leaks into the new one.
      closeWebSocket();
      fullResponseRef.current = '';
      reasoningRef.current = '';
      toolEventsRef.current = [];
      isGeneratingRef.current = false;
      currentTurnIdRef.current = null;
      snapshotSeqRef.current = -1;
      if (sendResolveRef.current) {
        sendResolveRef.current();
        sendResolveRef.current = null;
      }
      lastMessageIdRef.current = null;
      setStreaming({ response: undefined, isGenerating: false });
      setSession(prev => ({
        ...prev,
        sessionId: newSessionId,
        status: newSessionId ? 'active' : 'idle',
        messages: [],
        error: null,
      }));
    }
    if (!newSessionId) return;
    // Connect on mount too — not just on sessionId change. Otherwise a page
    // refresh during an in-flight turn leaves the WS closed, so the runner's
    // `done` event (and the final assistant message) never reaches the UI
    // until the user refreshes again.
    if (wsRef.current?.readyState !== WebSocket.OPEN && wsRef.current?.readyState !== WebSocket.CONNECTING) {
      connectWebSocket(newSessionId).catch(() => {});
    }
  }, [defaultOptions?.sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => { closeWebSocket(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -------------------------------------------------------------------------
  // WebSocket lifecycle (observer only)
  // -------------------------------------------------------------------------

  const closeWebSocket = useCallback(() => {
    if (connectAbortRef.current) {
      connectAbortRef.current.abort();
      connectAbortRef.current = null;
    }
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (wsRef.current) {
      try {
        const ws = wsRef.current;
        ws.onopen = null;
        ws.onmessage = null;
        ws.onerror = null;
        ws.onclose = null;
        if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
          ws.close(1000);
        }
      } catch {
        // already closed
      }
      wsRef.current = null;
    }
  }, []);

  const connectWebSocket = useCallback(async (sessionId: string): Promise<void> => {
    closeWebSocket();
    const controller = new AbortController();
    connectAbortRef.current = controller;
    const token = await getValidAccessToken();
    if (controller.signal.aborted) return;
    const url = buildWsUrl(sessionId, token, lastMessageIdRef.current);

    return new Promise<void>((resolve, reject) => {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        reconnectAttemptsRef.current = 0;
        authRefreshAttemptsRef.current = 0;
        resolve();
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data) as Record<string, unknown>;
          handleWsMessage(msg);
        } catch {
          // ignore malformed
        }
      };

      ws.onerror = () => {
        reject(new Error('WebSocket connection failed'));
      };

      ws.onclose = (event) => {
        wsRef.current = null;
        // Clean close (NORMAL / GOING_AWAY) — nothing to recover.
        if (event.code === WebSocketCloseCode.NORMAL || event.code === WebSocketCloseCode.GOING_AWAY) {
          return;
        }
        // Token expired/invalid on a long-lived socket: refresh + reconnect
        // immediately rather than burning a network-reconnect attempt.
        if (event.code === WebSocketCloseCode.EXPIRED_TOKEN || event.code === WebSocketCloseCode.INVALID_TOKEN) {
          reconnectAfterAuthExpiry(sessionId);
          return;
        }
        attemptReconnect(sessionId);
      };
    });
  }, [closeWebSocket]); // eslint-disable-line react-hooks/exhaustive-deps

  const attemptReconnect = useCallback((sessionId: string) => {
    // Offline: don't fire reconnects into the void or exhaust the attempt
    // budget. The onlineManager subscription below reconnects immediately when
    // connectivity returns. We deliberately KEEP the thinking indicator — the
    // turn runs server-side and resumes via ?since= replay on reconnect, so
    // this is a pause, not a loss. (Distinct from the exhausted-while-online
    // path, which clears the phantom spinner below.)
    if (!onlineManager.isOnline()) {
      return;
    }
    if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
      // All reconnects failed — the in-flight turn (if any) is now
      // unrecoverable from this client. Clear the spinner so the user
      // isn't left staring at a phantom "Thinking…" indicator with no
      // way to know the connection is gone.
      isGeneratingRef.current = false;
      snapshotSeqRef.current = -1;
      fullResponseRef.current = '';
      reasoningRef.current = '';
      toolEventsRef.current = [];
      setStreaming({ response: undefined, isGenerating: false });
      if (sendResolveRef.current) {
        sendResolveRef.current();
        sendResolveRef.current = null;
      }
      setSession(prev => ({ ...prev, status: 'error', error: 'chat.errors.connectionLost' }));
      return;
    }
    const delay = RECONNECT_BASE_DELAY_MS * Math.pow(2, reconnectAttemptsRef.current);
    reconnectAttemptsRef.current++;
    reconnectTimerRef.current = setTimeout(async () => {
      try {
        await connectWebSocket(sessionId);
      } catch {
        attemptReconnect(sessionId);
      }
    }, delay);
  }, [connectWebSocket]);

  // Token expired/invalid mid-socket: refresh + reconnect without consuming a
  // network-reconnect attempt. connectWebSocket() calls getValidAccessToken(),
  // which refreshes an expired token before building the WS URL. Reconnect
  // immediately (no backoff) since the failure is auth, not the network. If the
  // refresh keeps yielding a rejected token (revoked session), fall back to the
  // normal reconnect path so the phantom spinner still eventually clears.
  const reconnectAfterAuthExpiry = useCallback((sessionId: string) => {
    if (authRefreshAttemptsRef.current >= MAX_AUTH_REFRESH_ATTEMPTS) {
      attemptReconnect(sessionId);
      return;
    }
    authRefreshAttemptsRef.current++;
    connectWebSocket(sessionId).catch(() => attemptReconnect(sessionId));
  }, [connectWebSocket, attemptReconnect]);

  // Reconnect immediately when connectivity returns, instead of waiting out the
  // exponential-backoff timer. onlineManager is the project's connectivity
  // source of truth (Capacitor-backed on native, browser online/offline events
  // on web) — `navigator.onLine` lies inside WKWebView, so we never read it
  // directly. See OfflineStatusContext.
  useEffect(() => {
    const unsubscribe = onlineManager.subscribe((online: boolean) => {
      if (!online) return;
      const sid = sessionIdRef.current;
      if (!sid) return;
      const ws = wsRef.current;
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      reconnectAttemptsRef.current = 0;
      authRefreshAttemptsRef.current = 0;
      connectWebSocket(sid).catch(() => attemptReconnect(sid));
    });
    return unsubscribe;
  }, [connectWebSocket, attemptReconnect]);

  // -------------------------------------------------------------------------
  // Event dispatch
  // -------------------------------------------------------------------------

  const rememberLastMessageId = (id: unknown) => {
    if (typeof id === 'string' && id.length > 0) {
      lastMessageIdRef.current = id;
    }
  };

  // True when a live delta is already reflected in the partial output rendered
  // from a `turn_snapshot` — same turn, seq at or below the snapshot's last_seq.
  // The backend forwarder also drops these, so this is a defensive second line
  // (e.g. against the dual chat-WS + automation-observer delivery path). Only
  // reads refs, so a [] dep useCallback never goes stale.
  const isDuplicateBySnapshotSeq = useCallback((msg: Record<string, unknown>): boolean => {
    if (snapshotSeqRef.current < 0) return false;
    if (typeof msg.seq !== 'number') return false;
    const turnId = (msg.turn_id as string) || null;
    return turnId === currentTurnIdRef.current && msg.seq <= snapshotSeqRef.current;
  }, []);

  const finalizeAssistantMessage = useCallback((msg: Record<string, unknown>, interrupted: boolean) => {
    const fullResponse = (msg.full_response as string) || fullResponseRef.current;

    for (const te of toolEventsRef.current) {
      if (te.status === 'running') {
        te.status = 'error';
        te.errorMessage = interrupted ? 'Turn interrupted before tool completed' : 'Stream ended before tool completed';
      }
    }

    const streamToolCalls: ToolCallData[] | undefined = toolEventsRef.current.length > 0
      ? toolEventsRef.current.map((te, i) => ({
          id: te.toolCallId || `stream-tc-${i}`,
          tool_name: te.toolName,
          module: te.module,
          status: te.status as ToolCallData['status'],
          latency_ms: te.latencyMs ?? null,
          error_message: te.errorMessage ?? null,
          call_order: i,
          entities: te.entities ?? null,
          approval_id: te.approvalId ?? null,
        }))
      : undefined;

    const messageId = (msg.message_id as string) || `assistant-${Date.now()}`;
    const messageSeq = msg.message_seq;
    const assistantMessage: ChatMessage = {
      id: messageId,
      role: 'assistant',
      content: fullResponse,
      timestamp: new Date().toISOString(),
      ...(typeof messageSeq === 'number' ? { seq: messageSeq } : {}),
      ...(streamToolCalls && { tool_calls: streamToolCalls }),
      ...(interrupted ? { interrupted: true } : {}),
    };

    rememberLastMessageId(messageId);

    // Merge by seq so a later reconnect-replay of the same persisted assistant
    // message dedups instead of duplicating.
    setSession(prev => ({
      ...prev,
      messages: mergeMessagesBySeq(prev.messages, [assistantMessage]),
    }));

    // Refresh any REST-backed chat transcript consumers (ai-chat's own list,
    // AgentChatPanel's `useAgentChat`, etc.) so the persisted assistant message
    // becomes visible once the streaming bubble clears.
    queryClient.invalidateQueries({ queryKey: ['chat', 'sessions'] });
    queryClient.invalidateQueries({ queryKey: ['agent-chats'] });
  }, [queryClient]);

  const finishGeneration = useCallback(() => {
    isGeneratingRef.current = false;
    currentTurnIdRef.current = null;
    snapshotSeqRef.current = -1;
    fullResponseRef.current = '';
    reasoningRef.current = '';
    toolEventsRef.current = [];
    setStreaming({ response: undefined, isGenerating: false });
    if (sendResolveRef.current) {
      sendResolveRef.current();
      sendResolveRef.current = null;
    }
  }, []);

  const handleWsMessage = useCallback((msg: Record<string, unknown>) => {
    const type = msg.type as string;

    switch (type) {
      case 'replay': {
        const raw = (msg.messages as Array<Record<string, unknown>>) || [];
        const messages: ChatMessage[] = raw.map((m, index) => ({
          id: (m.id as string) || `replay-${index}`,
          role: m.role as ChatMessage['role'],
          content: (m.content as string) || '',
          timestamp: (m.timestamp as string) || new Date().toISOString(),
          ...(typeof m.seq === 'number' ? { seq: m.seq as number } : {}),
          ...(Array.isArray(m.tool_calls) && m.tool_calls.length > 0
            ? { tool_calls: m.tool_calls as ToolCallData[] }
            : {}),
          ...(Array.isArray(m.attachment_file_ids) && (m.attachment_file_ids as string[]).length > 0
            ? { attachment_file_ids: m.attachment_file_ids as string[] }
            : {}),
          ...(m.is_compaction_summary ? { is_compaction_summary: true } : {}),
          ...(m.interrupted ? { interrupted: true } : {}),
        }));
        if (messages.length > 0) {
          rememberLastMessageId(messages[messages.length - 1].id);
        }
        // Replay is authoritative (DB), so it wins over optimistic/live copies;
        // mergeMessagesBySeq dedups by seq and drops the reconciled optimistic.
        setSession(prev => ({
          ...prev,
          messages: mergeMessagesBySeq(prev.messages, messages),
        }));
        // Reconcile a turn that terminated while we were disconnected. The
        // runner persists the assistant message ONLY at terminal points (done /
        // interrupted) — never mid-stream — so a replayed trailing *assistant*
        // message while we still think we're generating means we missed the live
        // `done`/`interrupted` during the gap. The persisted message is now in
        // the list, so just clear the streaming spinner (no duplicate finalize).
        // If the turn were still running the server would instead send
        // `turn_snapshot` / `turn_in_progress`. Match only 'assistant' (never a
        // 'system'/error row on a still-running turn). Reconnect-window companion
        // to attemptReconnect's exhausted-spinner clear: no phantom "Thinking…".
        if (isGeneratingRef.current && messages.length > 0) {
          if (messages[messages.length - 1].role === 'assistant') {
            finishGeneration();
          }
        }
        break;
      }

      case 'message_persisted': {
        // Server-canonical message (today: the user message, emitted right after
        // the runner persists it). Reconcile our optimistic copy into the
        // seq-keyed store by upserting the seq'd version — mergeMessagesBySeq
        // drops the seq-less optimistic twin (same role + content).
        const seq = msg.message_seq;
        if (typeof seq !== 'number') break;
        const persisted: ChatMessage = {
          id: (msg.message_id as string) || `persisted-${seq}`,
          role: (msg.role as ChatMessage['role']) || 'user',
          content: (msg.content as string) || '',
          timestamp: (msg.timestamp as string) || new Date().toISOString(),
          seq,
          ...(Array.isArray(msg.attachment_file_ids) && (msg.attachment_file_ids as string[]).length > 0
            ? { attachment_file_ids: msg.attachment_file_ids as string[] }
            : {}),
        };
        rememberLastMessageId(persisted.id);
        setSession(prev => ({
          ...prev,
          messages: mergeMessagesBySeq(prev.messages, [persisted]),
        }));
        break;
      }

      case 'turn_started': {
        currentTurnIdRef.current = (msg.turn_id as string) || null;
        snapshotSeqRef.current = -1;
        fullResponseRef.current = '';
        reasoningRef.current = '';
        toolEventsRef.current = [];
        isGeneratingRef.current = true;
        setStreaming({ response: '', isGenerating: true });
        // Drop any client-only error messages from the previous failed
        // turn — once the user has retried, the stale "AI service
        // encountered an error" bubble shouldn't keep haunting the
        // transcript next to the new successful reply.
        setSession(prev => {
          const cleaned = prev.messages.filter(m => !(m.id && String(m.id).startsWith('error-')));
          if (cleaned.length === prev.messages.length) return prev;
          return { ...prev, messages: cleaned, error: null };
        });
        break;
      }

      case 'turn_snapshot': {
        // Sent by the server when the client (re)connects mid-turn (browser
        // refresh, second device, panel reopen). Carries the turn's accumulated
        // partial output so we render it immediately instead of a blank spinner,
        // then continue appending live deltas (seq > last_seq) seamlessly.
        currentTurnIdRef.current = (msg.turn_id as string) || null;
        snapshotSeqRef.current = typeof msg.last_seq === 'number' ? msg.last_seq : -1;
        fullResponseRef.current = (msg.text as string) || '';
        reasoningRef.current = (msg.reasoning as string) || '';

        const rawTools = (msg.tools as Array<Record<string, unknown>>) || [];
        toolEventsRef.current = rawTools.map((t) => ({
          toolName: (t.tool_name as string) || '',
          module: (t.module as string) || '',
          status: ((t.status as string) || 'running') as ToolEvent['status'],
          toolCallId: (t.tool_call_id as string) || undefined,
          latencyMs: typeof t.latency_ms === 'number' ? t.latency_ms : undefined,
          errorMessage: (t.error as string) || undefined,
          entities: Array.isArray(t.entities) ? (t.entities as ToolEvent['entities']) : undefined,
          requiresApproval: typeof t.requires_approval === 'boolean' ? t.requires_approval : undefined,
          approvalId: (t.approval_id as string) || undefined,
        }));
        const activeTool = toolEventsRef.current.find(e => e.status === 'running');

        isGeneratingRef.current = true;
        setStreaming(prev => ({
          ...prev,
          response: fullResponseRef.current,
          reasoning: reasoningRef.current,
          toolEvents: [...toolEventsRef.current],
          activeToolName: activeTool?.toolName ?? null,
          isGenerating: true,
        }));
        break;
      }

      case 'turn_in_progress': {
        // Sent by the server when the client (re)connects mid-turn but nothing
        // is buffered yet (turn just started). No turn_id or accumulated deltas
        // — just show the thinking indicator until a snapshot or terminal event
        // arrives.
        isGeneratingRef.current = true;
        setStreaming(prev => ({ ...prev, isGenerating: true }));
        break;
      }

      case 'text_delta': {
        if (!isGeneratingRef.current) break;
        if (isDuplicateBySnapshotSeq(msg)) break;
        const content = msg.content as string;
        fullResponseRef.current += content;
        setStreaming(prev => ({ ...prev, response: fullResponseRef.current }));
        break;
      }

      case 'reasoning_delta': {
        if (!isGeneratingRef.current) break;
        if (isDuplicateBySnapshotSeq(msg)) break;
        const content = msg.content as string;
        reasoningRef.current += content;
        setStreaming(prev => ({ ...prev, reasoning: reasoningRef.current }));
        break;
      }

      case 'tool_start': {
        const toolCallId = msg.tool_call_id as string | undefined;
        // Dedup: the same event can arrive via the chat WS and the
        // automation-run observer WS (see `_forward` in
        // scheduled_task_execution_service.py). Also guards against reconnect
        // replay delivering events already captured.
        if (toolCallId && toolEventsRef.current.some(e => e.toolCallId === toolCallId)) {
          break;
        }
        const toolEvent: ToolEvent = {
          toolName: msg.tool_name as string,
          module: msg.module as string,
          status: 'running',
          toolCallId,
        };
        toolEventsRef.current = [...toolEventsRef.current, toolEvent];
        setStreaming(prev => ({
          ...prev,
          toolEvents: [...toolEventsRef.current],
          activeToolName: msg.tool_name as string,
        }));
        break;
      }

      case 'tool_end': {
        const toolName = msg.tool_name as string;
        const approvalResolved = msg.approval_resolved as boolean | undefined;
        const toolCallId = msg.tool_call_id as string | undefined;
        const events = toolEventsRef.current;
        let idx = -1;

        if (approvalResolved) {
          for (let i = events.length - 1; i >= 0; i--) {
            if ((toolCallId && events[i].toolCallId === toolCallId) ||
                (events[i].toolName === toolName && events[i].status === 'pending_approval')) {
              idx = i;
              break;
            }
          }
          if (toolCallId) {
            setSession(prev => ({
              ...prev,
              messages: prev.messages.map(m => {
                if (!m.tool_calls) return m;
                const updated = m.tool_calls.map(tc =>
                  tc.id === toolCallId
                    ? { ...tc, status: (msg.success as boolean) ? 'success' as const : 'error' as const, error_message: (msg.error as string) || null }
                    : tc
                );
                return updated !== m.tool_calls ? { ...m, tool_calls: updated } : m;
              }),
            }));
          }
        } else {
          for (let i = events.length - 1; i >= 0; i--) {
            if (events[i].toolName === toolName && events[i].status === 'running') {
              idx = i;
              break;
            }
          }
        }

        if (idx >= 0) {
          const requiresApproval = msg.requires_approval as boolean | undefined;
          events[idx] = {
            ...events[idx],
            status: requiresApproval ? 'pending_approval' : (msg.success as boolean) ? 'success' : 'error',
            latencyMs: msg.latency_ms as number | undefined,
            toolCallId: toolCallId || events[idx].toolCallId,
            errorMessage: msg.error as string | undefined,
            entities: msg.entities as ToolEvent['entities'],
            requiresApproval: approvalResolved ? false : requiresApproval,
            approvalId: approvalResolved ? events[idx].approvalId : (msg.approval_id as string | undefined),
          };
        }
        toolEventsRef.current = [...events];
        const stillRunning = events.find(e => e.status === 'running');
        setStreaming(prev => ({
          ...prev,
          toolEvents: [...toolEventsRef.current],
          activeToolName: stillRunning?.toolName ?? null,
        }));
        break;
      }

      case 'approval_required': {
        // Runner persisted the assistant message (with any pre-tool text + the
        // pending tool_call) and emitted approval_required in lieu of `done`.
        // Finalize the streaming bubble into the messages list so the text
        // the model produced before the tool call isn't lost.
        finalizeAssistantMessage(msg, false);
        finishGeneration();
        break;
      }

      case 'done': {
        finalizeAssistantMessage(msg, false);
        finishGeneration();
        break;
      }

      case 'interrupted': {
        finalizeAssistantMessage(msg, true);
        finishGeneration();
        break;
      }

      case 'error_notice': {
        // NON-terminal. The backend is retrying, or the agent loop recovered
        // from a failed tool by feeding the error back to the model — the turn
        // is still streaming. So: show the notice, but do NOT finishGeneration()
        // (that would make every later text_delta hit the `!isGeneratingRef`
        // guard and silently truncate the reply) and do NOT set session.error
        // (that is the turn's terminal failure).
        //
        // The `error-` id prefix is load-bearing: `turn_started` clears stale
        // bubbles by that prefix, so a notice does not haunt the next turn.
        // Translate by code, exactly like the terminal `error` case; the
        // backend's English message is the fallback for a code with no
        // translation yet. `details` carries interpolation params (the failing
        // tool name for a TOOL_ERROR_* notice).
        const noticeMsg: ChatMessage = {
          id: `error-notice-${nextNoticeId()}`,
          role: 'system',
          content: translateErrorCode(
            msg.code as string | undefined,
            msg.message as string,
            msg.details as Record<string, unknown> | undefined,
          ),
          timestamp: new Date().toISOString(),
        };
        setSession(prev => ({ ...prev, messages: [...prev.messages, noticeMsg] }));
        break;
      }

      case 'error': {
        // TERMINAL — the turn is over, so this case ends generation.
        // Translate by code per the error-handling contract; the backend's
        // English message is the fallback for a code with no translation yet.
        const errorMessage = translateErrorCode(
          msg.code as string | undefined,
          msg.message as string
        );
        const errorMsg: ChatMessage = {
          id: `error-${nextNoticeId()}`,
          role: 'system',
          content: errorMessage,
          timestamp: new Date().toISOString(),
        };
        setSession(prev => ({
          ...prev,
          error: errorMessage,
          messages: [...prev.messages, errorMsg],
        }));
        finishGeneration();
        break;
      }

      case 'pong':
        break;

      default:
        // Unknown event — ignore
        break;
    }
  }, [finalizeAssistantMessage, finishGeneration, isDuplicateBySnapshotSeq]);

  const injectEvent = useCallback((event: Record<string, unknown>) => {
    handleWsMessage(event);
  }, [handleWsMessage]);

  // -------------------------------------------------------------------------
  // Session CRUD (REST)
  // -------------------------------------------------------------------------

  const createSession = useCallback(async (options?: ChatSessionOptions): Promise<string | null> => {
    try {
      setSession(prev => ({ ...prev, status: 'connecting', error: null }));

      const modelCategory = options?.modelCategory ?? defaultOptions?.modelCategory ?? pendingModelCategoryRef.current ?? undefined;
      const response = await axiosInstance.post('/api/v1/ai-chat/sessions', {
        chat_type: options?.chatType || defaultOptions?.chatType || 'assistant',
        context: options?.context || defaultOptions?.context || {},
        about_entities: options?.aboutEntities || defaultOptions?.aboutEntities || [],
        title: options?.title || defaultOptions?.title,
        ...(modelCategory !== undefined ? { model_category: modelCategory } : {}),
      });

      const sessionData = response.data.data;
      lastMessageIdRef.current = null;

      try {
        await connectWebSocket(sessionData.id);
      } catch {
        console.warn('[ChatSession] WebSocket connection failed');
      }

      setSession(prev => ({
        ...prev,
        sessionId: sessionData.id,
        chatType: sessionData.chat_type,
        status: 'active',
        messages: [],
        enabledModules: sessionData.enabled_modules || [],
        acceptedMimeTypes: sessionData.accepted_mime_types || [],
      }));

      return sessionData.id;
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'chat.errors.createSession';
      setSession(prev => ({ ...prev, status: 'error', error: msg }));
      return null;
    }
  }, [defaultOptions?.chatType, defaultOptions?.context, defaultOptions?.aboutEntities, defaultOptions?.title, defaultOptions?.modelCategory, connectWebSocket]);

  const loadSession = useCallback(async (sessionId: string): Promise<void> => {
    try {
      setSession(prev => ({ ...prev, status: 'connecting', error: null }));

      const response = await axiosInstance.get(`/api/v1/ai-chat/sessions/${sessionId}`);
      const data = response.data.data;

      const serverMessages: ChatMessage[] = mergeMessagesBySeq((data.messages || []).map((msg: {
        id?: string;
        role: string;
        content: string;
        seq?: number;
        timestamp?: string;
        tool_calls?: ToolCallData[];
        attachment_file_ids?: string[];
        is_compaction_summary?: boolean;
        interrupted?: boolean;
      }, index: number) => ({
        id: msg.id || `${sessionId}-${index}`,
        role: msg.role as ChatMessage['role'],
        content: msg.content,
        timestamp: msg.timestamp || new Date().toISOString(),
        ...(typeof msg.seq === 'number' ? { seq: msg.seq } : {}),
        ...(msg.tool_calls?.length && { tool_calls: msg.tool_calls }),
        ...(msg.attachment_file_ids && Array.isArray(msg.attachment_file_ids) && msg.attachment_file_ids.length > 0
          ? { attachment_file_ids: msg.attachment_file_ids as string[] }
          : {}),
        ...(msg.is_compaction_summary ? { is_compaction_summary: true } : {}),
        ...(msg.interrupted ? { interrupted: true } : {}),
      })));

      const lastId = serverMessages.length > 0 ? serverMessages[serverMessages.length - 1].id : null;
      lastMessageIdRef.current = lastId ?? null;

      const alreadyConnected = wsRef.current
        && wsRef.current.readyState === WebSocket.OPEN
        && session.sessionId === sessionId;
      if (!alreadyConnected) {
        try {
          await connectWebSocket(sessionId);
        } catch {
          console.warn('[ChatSession] WebSocket connection failed');
        }
      }

      setSession(prev => ({
        ...prev,
        sessionId,
        chatType: data.chat_type,
        status: 'active',
        // Reloading the SAME session: merge so an in-flight optimistic message
        // survives (reconciled by seq once its server twin loads). Switching
        // sessions: replace — prev.messages belongs to the old session.
        messages: prev.sessionId === sessionId
          ? mergeMessagesBySeq(prev.messages, serverMessages)
          : serverMessages,
        enabledModules: data.enabled_modules || [],
        acceptedMimeTypes: data.accepted_mime_types || [],
      }));
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'chat.errors.loadSession';
      setSession(prev => ({ ...prev, status: 'error', error: msg }));
    }
  }, [connectWebSocket, session.sessionId]);

  // -------------------------------------------------------------------------
  // Messaging — POST always, WS is pure observer
  // -------------------------------------------------------------------------

  const sendMessage = useCallback(async (message: string, messageContext?: Record<string, unknown>, fileIds?: string[], overrideSessionId?: string): Promise<void> => {
    if (!message.trim() && (!fileIds || fileIds.length === 0)) return;

    let currentSessionId = overrideSessionId || session.sessionId;
    if (!currentSessionId) {
      const dynamicContext = sessionContextRef.current;
      currentSessionId = await createSession(dynamicContext ? { context: dynamicContext } : undefined);
      if (!currentSessionId) return;
    }

    // Optimistic user bubble — no seq yet. The runner's `message_persisted`
    // event (or replay on reconnect) delivers the server seq, at which point
    // mergeMessagesBySeq drops this seq-less twin (same role + content).
    const userMessage: ChatMessage = {
      id: `optimistic-user-${Date.now()}`,
      role: 'user',
      content: message,
      timestamp: new Date().toISOString(),
      ...(fileIds && fileIds.length > 0 ? { attachment_file_ids: fileIds } : {}),
    };

    setSession(prev => ({
      ...prev,
      ...(overrideSessionId ? { sessionId: overrideSessionId, status: 'active' as const } : {}),
      messages: mergeMessagesBySeq(prev.messages, [userMessage]),
    }));

    fullResponseRef.current = '';
      reasoningRef.current = '';
    toolEventsRef.current = [];
    isGeneratingRef.current = true;
    snapshotSeqRef.current = -1;
    setStreaming({ response: '', isGenerating: true });

    // Ensure an observer WS is (or will be) connected so we get events.
    if (wsRef.current?.readyState !== WebSocket.OPEN) {
      connectWebSocket(currentSessionId).catch(() => {});
    }

    try {
      const body: Record<string, unknown> = { message };
      if (messageContext) body.message_context = messageContext;
      if (fileIds?.length) body.file_ids = fileIds;

      await axiosInstance.post(
        `/api/v1/ai-chat/sessions/${currentSessionId}/messages`,
        body,
      );

      // Resolve when the runner emits done / interrupted / error / approval_required.
      await new Promise<void>((resolve) => {
        sendResolveRef.current = resolve;
      });
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'chat.errors.sendMessage';
      setSession(prev => ({ ...prev, error: msg }));
      finishGeneration();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.sessionId, createSession, connectWebSocket, finishGeneration]);

  // -------------------------------------------------------------------------
  // Stop / End / Reset
  // -------------------------------------------------------------------------

  const stopGeneration = useCallback(async (): Promise<void> => {
    if (!session.sessionId) return;
    try {
      await axiosInstance.post(`/api/v1/ai-chat/sessions/${session.sessionId}/interrupt`, {});
    } catch {
      // non-fatal — runner will eventually time out
    }
    // Actual UI reset happens when the runner emits `interrupted`.
  }, [session.sessionId]);

  const endSession = useCallback(async (): Promise<void> => {
    if (!session.sessionId) return;
    closeWebSocket();
    try {
      await axiosInstance.delete(`/api/v1/ai-chat/sessions/${session.sessionId}`);
      setSession(prev => ({ ...prev, sessionId: null, status: 'idle' }));
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'chat.errors.endSession';
      setSession(prev => ({ ...prev, error: msg }));
    }
  }, [session.sessionId, closeWebSocket]);

  const reset = useCallback(() => {
    closeWebSocket();
    lastMessageIdRef.current = null;
    setSession({
      sessionId: null,
      chatType: defaultOptions?.chatType || 'assistant',
      status: 'idle',
      messages: [],
      error: null,
      enabledModules: [],
      acceptedMimeTypes: [],
    });
    setStreaming({ response: undefined, isGenerating: false });
  }, [defaultOptions?.chatType, closeWebSocket]);

  const updateToolCallStatus = useCallback((toolCallId: string, status: ToolCallData['status']) => {
    setSession(prev => ({
      ...prev,
      messages: prev.messages.map(m => {
        if (!m.tool_calls) return m;
        const updated = m.tool_calls.map(tc =>
          tc.id === toolCallId ? { ...tc, status } : tc
        );
        return updated !== m.tool_calls ? { ...m, tool_calls: updated } : m;
      }),
    }));
  }, []);

  return {
    session,
    streaming,
    createSession,
    loadSession,
    sendMessage,
    acceptedMimeTypes: session.acceptedMimeTypes,
    stopGeneration,
    endSession,
    reset,
    sessionContextRef,
    pendingModelCategoryRef,
    wsRef,
    injectEvent,
    updateToolCallStatus,
  };
}
